import React from 'react';
import { useSelector } from 'react-redux';
import {
    selectRouteType,
    selectStartPoint,
    selectEndPoint,
    selectViaPoints,
    selectRouteSegments,
    selectRouteDistance,
    selectRouteTime,
    selectWorkingRules,
    selectWorkingNotifs
} from '../../../features/tripcenter/googleRouteMakerSlice';

// MUI components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

// Icons
import TripOriginIcon from '@mui/icons-material/TripOrigin';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import RouteIcon from '@mui/icons-material/Route';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SpeedIcon from '@mui/icons-material/Speed';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import NotificationsIcon from '@mui/icons-material/Notifications';

const RouteSummary = () => {
    // Get route data from Redux
    const routeType = useSelector(selectRouteType);
    const startPoint = useSelector(selectStartPoint);
    const endPoint = useSelector(selectEndPoint);
    const viaPoints = useSelector(selectViaPoints);
    const routeSegments = useSelector(selectRouteSegments);
    const routeDistance = useSelector(selectRouteDistance);
    const routeTime = useSelector(selectRouteTime);
    const rules = useSelector(selectWorkingRules);
    const notifications = useSelector(selectWorkingNotifs);

    // Format route distance and time
    const formattedDistance = (routeDistance / 1000).toFixed(1);
    const formattedTime = formatTime(routeTime);

    function formatTime(seconds) {
        if (!seconds) return '0 min';

        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);

        if (hours > 0) {
            return `${hours} hr ${minutes} min`;
        } else {
            return `${minutes} min`;
        }
    }

    // Helper function to get route type label
    const getRouteTypeLabel = () => {
        switch (routeType) {
            case 'oneWayDirect':
                return 'One Way Direct';
            case 'roundTrip':
                return 'Round Trip';
            case 'oneWayWithStops':
                return 'One Way with Stops';
            case 'roundTripWithStops':
                return 'Round Trip with Stops';
            default:
                return 'Unknown';
        }
    };

    // Count notifications by type
    const countNotifications = () => {
        const counts = {
            total: 0,
            email: 0,
            sms: 0,
            whatsApp: 0,
            telegram: 0,
            push: 0
        };

        if (notifications) {
            if (notifications.email) counts.email = notifications.email.length;
            if (notifications.sms) counts.sms = notifications.sms.length;
            if (notifications.whatsApp) counts.whatsApp = notifications.whatsApp.length;
            if (notifications.telegram) counts.telegram = notifications.telegram.length;
            if (notifications.push) counts.push = notifications.push.length;

            counts.total = counts.email + counts.sms + counts.whatsApp + counts.telegram + counts.push;
        }

        return counts;
    };

    const notificationCounts = countNotifications();

    return (
        <Box p={3}>
            <Typography variant="h6" gutterBottom>
                Route Summary
            </Typography>

            <Typography variant="body1" color="textSecondary" paragraph>
                Review your route details before saving. Make sure everything is configured correctly.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 1 }}>
                {/* Basic route info */}
                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardHeader
                            title="Route Information"
                            titleTypographyProps={{ variant: 'subtitle1' }}
                            avatar={<RouteIcon />}
                            sx={{ bgcolor: 'action.hover', pb: 1 }}
                        />
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <Typography variant="body2" color="text.secondary" sx={{ width: 100 }}>
                                    Route Type:
                                </Typography>
                                <Chip
                                    label={getRouteTypeLabel()}
                                    color="primary"
                                    size="small"
                                />
                            </Box>

                            <Box display="flex" alignItems="center" mb={2}>
                                <Typography variant="body2" color="text.secondary" sx={{ width: 100 }}>
                                    Distance:
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {formattedDistance} km
                                </Typography>
                            </Box>

                            <Box display="flex" alignItems="center">
                                <Typography variant="body2" color="text.secondary" sx={{ width: 100 }}>
                                    Est. Time:
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {formattedTime}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Locations */}
                <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardHeader
                            title="Locations"
                            titleTypographyProps={{ variant: 'subtitle1' }}
                            avatar={<LocationOnIcon />}
                            sx={{ bgcolor: 'action.hover', pb: 1 }}
                        />
                        <CardContent sx={{ pt: 0 }}>
                            <List dense disablePadding>
                                <ListItem>
                                    <ListItemIcon>
                                        <TripOriginIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Start"
                                        secondary={startPoint?.locationName}
                                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                                        secondaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
                                    />
                                </ListItem>

                                {viaPoints.length > 0 && viaPoints.map((point, index) => (
                                    <ListItem key={index}>
                                        <ListItemIcon>
                                            <AddLocationAltIcon color="info" fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={`Via Point ${index + 1}`}
                                            secondary={point.locationName}
                                            primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                                            secondaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
                                        />
                                    </ListItem>
                                ))}

                                <ListItem>
                                    <ListItemIcon>
                                        <LocationOnIcon color="secondary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={routeType === 'roundTrip' || routeType === 'roundTripWithStops' ? 'Destination' : 'End'}
                                        secondary={endPoint?.locationName}
                                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                                        secondaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Segments */}
                <Grid item xs={12}>
                    <Card variant="outlined">
                        <CardHeader
                            title="Route Segments"
                            titleTypographyProps={{ variant: 'subtitle1' }}
                            avatar={<AltRouteIcon />}
                            sx={{ bgcolor: 'action.hover', pb: 1 }}
                        />
                        <CardContent sx={{ p: 0 }}>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>#</TableCell>
                                            <TableCell>From → To</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Distance</TableCell>
                                            <TableCell>Load Type</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {routeSegments.map((segment, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>
                                                    {segment.startPoint?.locationName || 'Start'} → {segment.endPoint?.locationName || 'End'}
                                                </TableCell>
                                                <TableCell>{segment.name}</TableCell>
                                                <TableCell>
                                                    {(routeDistance / 1000 / routeSegments.length).toFixed(1)} km
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={<LocalShippingIcon />}
                                                        label={segment.loadType === 'loaded' ? 'Loaded' : 'Empty'}
                                                        size="small"
                                                        color={segment.loadType === 'loaded' ? 'success' : 'default'}
                                                        variant={segment.loadType === 'loaded' ? 'filled' : 'outlined'}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Rules */}
                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardHeader
                            title="Monitoring Rules"
                            titleTypographyProps={{ variant: 'subtitle1' }}
                            avatar={<SpeedIcon />}
                            sx={{ bgcolor: 'action.hover', pb: 1 }}
                        />
                        <CardContent>
                            {rules ? (
                                <List dense>
                                    {rules.speedLimit && (
                                        <ListItem>
                                            <ListItemIcon>
                                                <SpeedIcon color="primary" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Speed Limit"
                                                secondary={`${rules.speedLimit} km/h`}
                                            />
                                        </ListItem>
                                    )}

                                    {rules.routeViolationThreshold && (
                                        <ListItem>
                                            <ListItemIcon>
                                                <AltRouteIcon color="primary" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Route Violation Threshold"
                                                secondary={`${rules.routeViolationThreshold} km`}
                                            />
                                        </ListItem>
                                    )}

                                    {rules.maxHaltTime && (
                                        <ListItem>
                                            <ListItemIcon>
                                                <WatchLaterIcon color="primary" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Max Halt Time"
                                                secondary={`${rules.maxHaltTime} hours`}
                                            />
                                        </ListItem>
                                    )}

                                    {rules.drivingStartTime && rules.drivingEndTime && (
                                        <ListItem>
                                            <ListItemIcon>
                                                <AccessTimeIcon color="primary" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Driving Hours"
                                                secondary={`${rules.drivingStartTime} to ${rules.drivingEndTime}`}
                                            />
                                        </ListItem>
                                    )}

                                    {rules.minTemperature && rules.maxTemperature && (
                                        <ListItem>
                                            <ListItemIcon>
                                                <ThermostatIcon color="primary" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Temperature Range"
                                                secondary={`${rules.minTemperature}°C to ${rules.maxTemperature}°C`}
                                            />
                                        </ListItem>
                                    )}

                                    {rules.minFuelEfficiency && (
                                        <ListItem>
                                            <ListItemIcon>
                                                <LocalGasStationIcon color="primary" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Fuel Efficiency"
                                                secondary={`Min ${rules.minFuelEfficiency} km/L (Max ${rules.maxFuelConsumption} L)`}
                                            />
                                        </ListItem>
                                    )}
                                </List>
                            ) : (
                                <Typography variant="body2" color="text.secondary" align="center">
                                    No monitoring rules have been set
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Notifications */}
                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardHeader
                            title="Notifications"
                            titleTypographyProps={{ variant: 'subtitle1' }}
                            avatar={<NotificationsActiveIcon />}
                            sx={{ bgcolor: 'action.hover', pb: 1 }}
                        />
                        <CardContent>
                            {notificationCounts.total > 0 ? (
                                <Grid container spacing={2}>
                                    {notificationCounts.email > 0 && (
                                        <Grid item xs={6}>
                                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                                <EmailIcon color="primary" />
                                                <Typography variant="body2" sx={{ mt: 1 }}>
                                                    {notificationCounts.email} Email
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    )}

                                    {notificationCounts.sms > 0 && (
                                        <Grid item xs={6}>
                                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                                <SmsIcon color="primary" />
                                                <Typography variant="body2" sx={{ mt: 1 }}>
                                                    {notificationCounts.sms} SMS
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    )}

                                    {notificationCounts.whatsApp > 0 && (
                                        <Grid item xs={6}>
                                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                                <WhatsAppIcon color="primary" />
                                                <Typography variant="body2" sx={{ mt: 1 }}>
                                                    {notificationCounts.whatsApp} WhatsApp
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    )}

                                    {notificationCounts.telegram > 0 && (
                                        <Grid item xs={6}>
                                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                                <TelegramIcon color="primary" />
                                                <Typography variant="body2" sx={{ mt: 1 }}>
                                                    {notificationCounts.telegram} Telegram
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    )}

                                    {notificationCounts.push > 0 && (
                                        <Grid item xs={6}>
                                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                                <NotificationsIcon color="primary" />
                                                <Typography variant="body2" sx={{ mt: 1 }}>
                                                    {notificationCounts.push} Push
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    )}
                                </Grid>
                            ) : (
                                <Typography variant="body2" color="text.secondary" align="center">
                                    No notifications have been set
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Box mt={4} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                    Review all details above and click "Save Route" when ready.
                </Typography>
            </Box>
        </Box>
    );
};

export default RouteSummary; 